import type { ConsentSignals } from '@/lib/consent/consent-signals';

import type { AnalyticsAdapter, CaptureOptions } from './types';

import { stripPiiKeys } from './strip-pii';

/**
 * Type alias for the lazy-imported `@segment/analytics-next` module's
 * AnalyticsBrowser. Pulled in as a type only so TS knows the shape
 * without forcing the actual bundle into the eagerly-loaded chunk.
 *
 * In type position this resolves to the instance type, which is what
 * `AnalyticsBrowser.load(...)` returns. Do NOT add `typeof` — see the
 * same note in `segment-adapter.ts`.
 */
type SegmentAnalytics = import('@segment/analytics-next').AnalyticsBrowser;

/**
 * `AnalyticsBrowser` extends `Promise<[Analytics, Context]>`, so storing
 * one directly as the resolved value of an async function would
 * Promise-flatten the return type into the tuple. The holder preserves
 * the buffered-facade reference.
 */
interface FirstPartySdkHolder {
  browser: SegmentAnalytics;
}

interface FirstPartyAdapterConfig {
  /**
   * Source label our collector uses to attribute the stream. Not a
   * secret — it ships to the browser and is sent as HTTP Basic auth
   * user by the SDK's batch dispatcher.
   *
   * MUST differ from `SEGMENT_WRITE_KEY`: the SDK scopes its persisted
   * queues by write key (`{writeKey}:event-queue`,
   * `{writeKey}:dest-Segment.io`), so two instances sharing a key would
   * share localStorage retry buffers and steal/double-deliver each
   * other's events.
   */
  writeKey: string;
  /**
   * Collector host **and base path, without a scheme** — e.g.
   * `t.respondent.io/v1`. The SDK builds request URLs as
   * `${protocol}://${apiHost}/${path}`, so a scheme here would produce
   * `https://https://…`. With batching enabled every event lands on a
   * single `POST ${apiHost}/b`.
   */
  apiHost: string;
}

/**
 * Module-scoped promise so multiple adapter instances on the same page
 * cannot trigger duplicate `AnalyticsBrowser.load` calls. Separate from
 * the Segment adapter's own module-scoped promise — that's what lets the
 * two adapters hold two independent `AnalyticsBrowser` instances during
 * the dual-write window without either refactoring the other.
 */
let firstPartyLoadPromise: Promise<FirstPartySdkHolder> | null = null;

/**
 * Module-scoped middleware install guard. The consent middleware must
 * fire exactly once per loaded SDK regardless of how many adapter
 * instances trigger the load — otherwise multi-instance paths would
 * stack middlewares, each closing over its own instance's stale
 * consent, and drop events unexpectedly after opt-in.
 *
 * The in-flight promise rather than a boolean, because the install is async:
 * a second instance arriving mid-install awaits this one instead of starting
 * its own, and a rejection resets it to `null` so the next load retries. A
 * boolean set before the call could only claim "someone started it", which is
 * not the same claim as "the gate exists".
 */
let firstPartyMiddlewareInstall: Promise<void> | null = null;

/**
 * Module-scoped consent state, read by the single installed middleware
 * so any instance's `optIn`/`optOut` is observed.
 */
let firstPartyConsent: 'in' | 'out' | null = null;

function loadFirstPartySdk(
  config: FirstPartyAdapterConfig,
): Promise<FirstPartySdkHolder> {
  if (firstPartyLoadPromise) return firstPartyLoadPromise;
  firstPartyLoadPromise = (async () => {
    try {
      const { AnalyticsBrowser } = await import('@segment/analytics-next');
      return {
        browser: AnalyticsBrowser.load(
          {
            writeKey: config.writeKey,
            // Supplying `cdnSettings` inline is what keeps this instance
            // off cdn.segment.com — `load` only fetches remote settings
            // when this is absent.
            //
            // `Segment.io` must be present here even though the second
            // argument also carries apiHost: the SDK's `mergedOptions`
            // iterates the CDN settings' integrations and merges init
            // overrides ON TOP of them, so an integration missing from
            // cdnSettings gets NO settings object at all — apiHost
            // would silently fall back to the `api.segment.io/v1`
            // default. An empty `integrations: {}` ships events to
            // Segment.
            cdnSettings: {
              integrations: {
                'Segment.io': {
                  apiKey: config.writeKey,
                  apiHost: config.apiHost,
                  protocol: 'https',
                },
              },
              // The SDK's own diagnostic counters (`analytics_js.*`,
              // posted to `${apiHost}/m`) are meaningless to our
              // collector. `Stats.initRemoteMetrics` writes
              // module-global state that the last `load` on the page
              // wins, so this cannot be guaranteed to hold for the
              // Segment instance too — but either winner keeps
              // diagnostics off our collector: ours sends nothing,
              // theirs sends to Segment as before.
              metrics: { sampleRate: 0 },
            },
          },
          {
            integrations: {
              'Segment.io': {
                apiHost: config.apiHost,
                protocol: 'https',
                // Batching also collapses delivery onto a single
                // endpoint, and switches unload flushes to `keepalive`.
                deliveryStrategy: { strategy: 'batching' },
              },
            },
          },
        ),
      };
    } catch (error) {
      // Clear the cached promise so a subsequent routed call can retry
      // the load instead of awaiting an already-rejected promise.
      firstPartyLoadPromise = null;
      throw error;
    }
  })();
  return firstPartyLoadPromise;
}

/** Test-only reset hook — clears module-scoped state between tests. */
export function __resetFirstPartyAdapterModuleStateForTesting() {
  firstPartyLoadPromise = null;
  firstPartyMiddlewareInstall = null;
  firstPartyConsent = null;
}

/**
 * First-party analytics adapter: the same `@segment/analytics-next`
 * client, pointed at our own collector instead of Segment's tracking
 * API. It runs alongside `SegmentAdapter` during the migration window
 * and becomes the only pipe once the Segment source is switched off.
 *
 * ## Why reuse the Segment SDK
 *
 * The wire format, the retry/batch queue, the `page`/`track`/`identify`
 * primitives and the `ajs_anonymous_id` identity cookie are all things
 * we would otherwise have to reimplement. Reusing the SDK also means the
 * cutover changes only which host receives the batch — event shape,
 * anonymous IDs and consent behaviour stay byte-identical, so warehouse
 * consumers see one continuous stream across the switch.
 *
 * ## Never contacts segment.com
 *
 * Two things are load-bearing, both verified against the SDK source
 * (`@segment/analytics-next@1.84`) rather than its docs:
 *
 * 1. Inline `cdnSettings` suppresses the remote settings fetch.
 * 2. `Segment.io` **inside** those cdnSettings is what makes the
 *    `apiHost` override take effect at all (see the comment on the load
 *    call). Getting this wrong fails open — straight to Segment.
 *
 * ## Coexistence with SegmentAdapter
 *
 * Two live `AnalyticsBrowser` instances on one page share no
 * client-side state except identity, which is exactly the split we
 * want:
 *
 * - **Shared (good):** `ajs_anonymous_id`, `ajs_user_id`,
 *   `ajs_user_traits` are fixed storage keys, so both pipes stamp the
 *   same anonymous and user IDs. Identity is continuous across the
 *   cutover.
 * - **Separate (required):** event and retry queues are namespaced by
 *   write key, so the two instances never touch each other's buffers —
 *   provided the write keys differ. See `writeKey` in the config type.
 * - **Global, last-load-wins:** the remote-metrics config and the
 *   `__SEGMENT_INSPECTOR__` devtools hook. Neither carries product
 *   events; see the `metrics` comment above.
 *
 * Consent, lifecycle and queue-drain semantics are otherwise identical
 * to `SegmentAdapter` — including the source-middleware consent gate
 * that drops **every** event type (track, page, identify) after
 * `optOut()`. That file carries the full reasoning for those choices.
 */
export class FirstPartyAdapter implements AnalyticsAdapter {
  readonly name = 'firstParty' as const;

  private readonly writeKey: string;
  private readonly apiHost: string;

  private sdk: FirstPartySdkHolder | null = null;
  private loadStarted = false;

  /** Calls deferred until the SDK loads. */
  private readonly queue: Array<(sdk: FirstPartySdkHolder) => void> = [];

  constructor(config: FirstPartyAdapterConfig) {
    this.writeKey = config.writeKey;
    this.apiHost = config.apiHost;
  }

  // ── Routed methods: trigger lazy load on first call ─────────────────

  page(
    name: string,
    properties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    const stripped = stripPiiKeys(properties);
    const apply = (sdk: FirstPartySdkHolder) => {
      void sdk.browser.page(name, stripped);
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadFirstParty();
  }

  track(
    event: string,
    properties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    // As with Segment, `options.immediate` can't be honoured — the SDK
    // exposes no per-call sendBeacon transport. The batching delivery
    // strategy does flush with `keepalive` on `pagehide`, which covers
    // most unload cases.
    const stripped = stripPiiKeys(properties);
    const apply = (sdk: FirstPartySdkHolder) => {
      void sdk.browser.track(event, stripped);
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadFirstParty();
  }

  trackException(
    error: unknown,
    additionalProperties?: Record<string, unknown>,
    _options?: CaptureOptions,
  ) {
    // No first-class exception primitive in this wire format; emit a
    // track event so it still lands in the warehouse for joins.
    const description = error instanceof Error ? error.message : String(error);
    const stripped = stripPiiKeys(additionalProperties);
    const apply = (sdk: FirstPartySdkHolder) => {
      void sdk.browser.track('exception', { ...stripped, description });
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadFirstParty();
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    const apply = (sdk: FirstPartySdkHolder) => {
      void sdk.browser.identify(userId, traits);
    };
    if (this.sdk) {
      apply(this.sdk);
      return;
    }
    this.queue.push(apply);
    this.lazyLoadFirstParty();
  }

  // ── Lifecycle methods: update internal state, never trigger load ────

  reset() {
    if (this.sdk) {
      void this.sdk.browser.reset();
      return;
    }
    // Pre-load reset (e.g. logout before the SDK finishes importing):
    // drop queued calls so they don't replay afterwards and attribute
    // the new anonymous session to the prior identified user.
    this.queue.length = 0;
  }

  /**
   * The first-party pipe gates on a single consent flag read by the
   * middleware, so it follows `analytics_storage`. The ad signals are
   * meaningless here.
   */
  setConsent(signals: ConsentSignals) {
    if (signals.analytics_storage === 'granted') {
      this.optIn();
    } else {
      this.optOut();
    }
  }

  optIn() {
    firstPartyConsent = 'in';
  }

  optOut() {
    firstPartyConsent = 'out';
    this.queue.length = 0;
    if (this.sdk) void this.sdk.browser.reset();
  }

  toggleSessionRecording(_toState: 'on' | 'off') {
    // No session recording in this pipe; intentional no-op.
  }

  isReady(): boolean {
    return this.sdk !== null;
  }

  // ── Internal ────────────────────────────────────────────────────────

  /**
   * Triggers the page-level lazy load, then once the SDK is ready
   * installs the consent middleware (once per page) and drains queued
   * calls. Safe to call repeatedly.
   */
  private lazyLoadFirstParty(): void {
    if (this.loadStarted || globalThis.document === undefined) return;
    this.loadStarted = true;

    void (async () => {
      try {
        const sdk = await loadFirstPartySdk({
          writeKey: this.writeKey,
          apiHost: this.apiHost,
        });
        // Awaited, and before `this.sdk` is published. `addSourceMiddleware`
        // is async — it dynamic-imports before registering — so `void`-ing it
        // left events able to drain past a gate that did not exist yet, and a
        // rejection left the pipe permanently ungated: the flag stayed true,
        // nothing retried, and every later event reached the collector with no
        // consent check at all.
        //
        // The async wrapper (rather than Promise.resolve().then) is what
        // gives the guard its `Promise<void>` type without a value-discarding
        // then() for lint to argue with, and `await` inside it behaves the
        // same whether or not the SDK's return type is actually a thenable.
        firstPartyMiddlewareInstall ??= (async () => {
          await sdk.browser.addSourceMiddleware(({ payload, next }) => {
            if (firstPartyConsent === 'out') return;
            next(payload);
          });
        })().catch((error: unknown) => {
          // Release the guard so a later load can retry, then fail this load.
          firstPartyMiddlewareInstall = null;
          throw error;
        });
        await firstPartyMiddlewareInstall;

        // Only now is the pipe safe to use. Publishing the SDK earlier meant a
        // failed gate install still left `isReady()` true, so subsequent calls
        // bypassed the queue and went straight out ungated.
        this.sdk = sdk;

        // If consent flipped to 'out' while the SDK was importing, skip
        // the drain — optOut already cleared the queue, but be explicit
        // so a future caller that retains the queue can't leak.
        if (firstPartyConsent === 'out') {
          this.queue.length = 0;
          return;
        }

        // Per-call try/catch so one failing entry doesn't poison the
        // rest of the replay.
        for (const apply of this.queue) {
          try {
            apply(sdk);
          } catch (error) {
            console.error('First-party analytics queue drain error', error);
          }
        }
        this.queue.length = 0;
      } catch (error) {
        console.error('Failed to lazy-load first-party analytics', error);
        this.queue.length = 0;
        this.loadStarted = false;
      }
    })();
  }
}

/**
 * Factory: returns a `FirstPartyAdapter` only when BOTH the write key
 * and the collector host are configured, else `null` so the routing
 * layer filters it out. Both are required — a write key without a host
 * would fall back to the SDK's `api.segment.io/v1` default and quietly
 * send our events to Segment.
 */
export function createFirstPartyAdapter(env: {
  FIRST_PARTY_ANALYTICS_WRITE_KEY: string;
  FIRST_PARTY_ANALYTICS_HOST: string;
  SEGMENT_WRITE_KEY?: string;
}): AnalyticsAdapter | null {
  if (!env.FIRST_PARTY_ANALYTICS_WRITE_KEY) return null;
  if (!env.FIRST_PARTY_ANALYTICS_HOST) return null;
  // Two live `AnalyticsBrowser` instances on one write key share
  // `{writeKey}:event-queue` and `{writeKey}:dest-Segment.io` in localStorage,
  // so they steal and double-deliver each other's events. Refusing to start is
  // the only safe response: the whole point of dual-write is comparing the two
  // streams, and a shared key makes that comparison quietly wrong rather than
  // obviously broken.
  if (env.FIRST_PARTY_ANALYTICS_WRITE_KEY === env.SEGMENT_WRITE_KEY) {
    console.error(
      'First-party analytics disabled: FIRST_PARTY_ANALYTICS_WRITE_KEY must differ from SEGMENT_WRITE_KEY.',
    );
    return null;
  }
  return new FirstPartyAdapter({
    writeKey: env.FIRST_PARTY_ANALYTICS_WRITE_KEY,
    apiHost: env.FIRST_PARTY_ANALYTICS_HOST,
  });
}
